/**
 * ChainBadge — Bloomberg-light terminal chip.
 *
 * Tokens (DESIGN.md): JetBrains Mono · zero radius · hairline border ·
 * ink-2 text on subtle hi-vis amber tint for sui (state-bearing color,
 * NOT decorative), ink-2 on bone for solana (neutral).
 *
 * Renders as `[ solana ]` or `[ sui ]` — bracket marker matches the
 * `[ SECTION ]` pattern in the rest of the design system.
 */

import { type CSSProperties } from "react";

export type Chain = "solana" | "sui";

interface Props {
  chain: Chain;
  className?: string;
}

const baseStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  font: "600 10px/1 var(--font-mono, 'Spline Sans Mono', Menlo, monospace)",
  letterSpacing: "0.02em",
  padding: "3px 9px 3px 7px",
  border: "1px solid var(--line, rgba(27,22,64,0.16))",
  borderRadius: 999,
  color: "var(--slate, #56527E)",
  background: "var(--bg-elev, transparent)",
};

const suiStyle: CSSProperties = {
  ...baseStyle,
  // orange-peel accent — state-bearing, matches Persian+Orange tokens
  background: "color-mix(in srgb, var(--accent, #FF9F1C) 10%, transparent)",
  borderColor: "color-mix(in srgb, var(--accent, #FF9F1C) 38%, transparent)",
  color: "var(--accent-deep, #E07E00)",
};

const dotStyle = (color: string): CSSProperties => ({ width: 6, height: 6, borderRadius: "50%", background: color, flex: "none" });

export function ChainBadge({ chain, className }: Props) {
  const isSui = chain === "sui";
  return (
    <span className={className} style={isSui ? suiStyle : baseStyle} aria-label={`chain: ${chain}`}>
      <span style={dotStyle(isSui ? "var(--accent, #FF9F1C)" : "var(--slate, #56527E)")} aria-hidden />
      {isSui ? "Sui" : "Solana"}
    </span>
  );
}
